import { useAuth } from '../Context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useState, useEffect } from 'react';
import { ensureUserAccessForAuthenticatedUser } from '../lib/userAccessOrg';

export function useAuthAdmin() {
    const { user } = useAuth();
    const [userRole, setUserRole] = useState(null);
    const [usagePlan, setUsagePlan] = useState('free');
    const [onboardingState, setOnboardingState] = useState('completed');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const debugAuth = import.meta?.env?.DEV && localStorage.getItem('auth_debug') === '1';

    useEffect(() => {
        async function fetchUserRole() {
            if (!user) {
                setUserRole(null);
                setUsagePlan('free');
                setOnboardingState('completed');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                // First try to get existing role
                let existingUser = null;
                let fetchError = null;
                const initialResult = await supabase
                    .from('user_access')
                    .select('role, usage_plan, onboarding_state')
                    .eq('id', user.id)
                    .maybeSingle();
                existingUser = initialResult.data;
                fetchError = initialResult.error;

                // Backward compatibility: onboarding_state may not exist before migration.
                if (fetchError && String(fetchError.message || '').toLowerCase().includes('onboarding_state')) {
                    const fallbackResult = await supabase
                        .from('user_access')
                        .select('role, usage_plan')
                        .eq('id', user.id)
                        .maybeSingle();
                    existingUser = fallbackResult.data
                        ? { ...fallbackResult.data, onboarding_state: 'completed' }
                        : null;
                    fetchError = fallbackResult.error;
                }

                if (fetchError) throw fetchError;

                // If user doesn't exist, create new entry
                if (!existingUser) {
                    if (debugAuth) console.log('Ensuring new user access entry...');

                    const targetOrigin =
                        window.location.pathname === '/auth/callback'
                            ? new URLSearchParams(window.location.search).get('target')
                            : null;

                    const ensured = await ensureUserAccessForAuthenticatedUser({
                        supabaseClient: supabase,
                        authUser: user,
                        hostname: window.location.hostname,
                        targetOrigin,
                        debug: debugAuth,
                    });

                    let fallbackCreated = await supabase
                        .from('user_access')
                        .select('role, usage_plan, onboarding_state')
                        .eq('id', user.id)
                        .maybeSingle();

                    if (fallbackCreated.error && String(fallbackCreated.error.message || '').toLowerCase().includes('onboarding_state')) {
                        fallbackCreated = await supabase
                            .from('user_access')
                            .select('role, usage_plan')
                            .eq('id', user.id)
                            .maybeSingle();
                        if (fallbackCreated.data) {
                            fallbackCreated = {
                                ...fallbackCreated,
                                data: { ...fallbackCreated.data, onboarding_state: 'completed' }
                            };
                        }
                    }

                    const safeNewUser = fallbackCreated.data
                        ? { ...fallbackCreated.data, onboarding_state: fallbackCreated.data.onboarding_state || 'completed' }
                        : null;
                    const safeInsertError = fallbackCreated.error;

                    if (safeInsertError || !safeNewUser) {
                        console.error('Error creating user access:', safeInsertError || ensured);
                        setError('Failed to create user access');
                        setUserRole(null);
                    } else {
                        if (debugAuth) console.log('New user access created:', safeNewUser);
                        setUserRole(safeNewUser.role);
                        setUsagePlan(safeNewUser.usage_plan || 'free');
                        setOnboardingState(safeNewUser.onboarding_state || 'completed');
                        setError(null);
                    }
                } else {
                    if (debugAuth) console.log('Existing user access found:', existingUser);
                    setUserRole(existingUser.role);
                    setUsagePlan(existingUser.usage_plan || 'free');
                    setOnboardingState(existingUser.onboarding_state || 'completed');
                    setError(null);
                }
            } catch (error) {
                console.error('Error in user access:', error);
                setError(error.message);
                setUserRole(null);
                setUsagePlan('free');
                setOnboardingState('completed');
            } finally {
                setLoading(false);
            }
        }

        fetchUserRole();
    }, [user]);

    const isSuperAdmin = () => userRole === 'super_admin';
    const isAdmin = () => userRole === 'admin' || userRole === 'super_admin';
    const isCreator = () => userRole === 'creator';
    const isLearner = () => userRole === 'learner';
    const isTrainer = () => userRole === 'trainer';
    const isParent = () => userRole === 'parent';

    const updateUserRole = async (targetUserId, newRole) => {
        if (!isAdmin()) {
            throw new Error('Only admins can update roles');
        }

        const { data, error } = await supabase
            .from('user_access')
            .update({ role: newRole })
            .eq('id', targetUserId)
            .select();

        if (error) throw error;
        return data;
    };

    return {
        userRole,
        isSuperAdmin,
        isAdmin,
        isCreator,
        isLearner,
        isTrainer,
        isParent,
        usagePlan,
        onboardingState,
        updateUserRole,
        loading,
        error
    };
}
